/**
 * @fileoverview Crew Pipeline Check API Service
 * @description Flow CRUD, single-checkpoint dry runs, and run execution.
 *
 *              Run progress arrives over server-sent events. `EventSource`
 *              cannot attach an Authorization header, so the stream is read
 *              with `fetch` instead and callers fall back to polling when the
 *              stream is unavailable.
 */

import { apiClient } from '@/services';
import { APP_CONFIG } from '@/config';

const BASE = '/v1/crew-pipeline-check';

/** Split a raw SSE buffer into complete `event:`/`data:` frames. */
function parseFrames(buffer) {
  const frames = [];
  const chunks = buffer.split('\n\n');
  const remainder = chunks.pop() ?? '';

  for (const chunk of chunks) {
    let name = 'message';
    const data = [];
    for (const line of chunk.split('\n')) {
      if (line.startsWith('event:')) name = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trim());
    }
    if (!data.length) continue;
    try {
      frames.push({ name, payload: JSON.parse(data.join('\n')) });
    } catch {
      /* keep-alive comments and malformed frames are ignored */
    }
  }
  return { frames, remainder };
}

export const crewPipelineCheckApi = {
  health: () => apiClient.get(`${BASE}/health`),

  /** Checkpoint-kind schemas, token reference, and the built-in flow. */
  getCatalog: () => apiClient.get(`${BASE}/catalog`),

  /** An empty checkpoint of a given kind, for the "add checkpoint" dialog. */
  getBlankCheckpoint: (kind) => apiClient.get(`${BASE}/checkpoints/blank`, { kind }),

  listFlows: () => apiClient.get(`${BASE}/flows`),
  getFlow: (id) => apiClient.get(`${BASE}/flows/${id}`),
  saveFlow: (flow) => apiClient.post(`${BASE}/flows`, flow),
  deleteFlow: (id) => apiClient.delete(`${BASE}/flows/${id}`),

  /**
   * Query a single checkpoint without running the whole chain.
   * @param {Object} payload - { flight, date, station, airline, checkpoint, flow_id }
   */
  testCheckpoint: (payload) => apiClient.post(`${BASE}/checkpoints/test`, payload),

  /**
   * Run a flow and stream its progress.
   *
   * The same request runs the work and reports it, so there is no run id to
   * poll and nothing stored server-side. Aborting the signal cancels the run.
   *
   * @param {Object} payload - { flight, date, station, airline, flow | flow_id }
   * @param {{onAccepted: Function, onEvent: Function, onDone: Function, signal: AbortSignal}} handlers
   * @returns {Promise<void>}
   * @throws {Error} If the run is rejected before it starts.
   */
  runFlow: async (payload, { onAccepted, onEvent, onDone, signal }) => {
    const response = await fetch(`${APP_CONFIG.api.baseUrl}${BASE}/runs/stream`, {
      method: 'POST',
      headers: { ...apiClient.getHeaders(), Accept: 'text/event-stream' },
      body: JSON.stringify(payload),
      signal,
    });

    if (!response.ok) {
      const problem = await response.json().catch(() => ({}));
      throw new Error(problem.error || problem.message || `HTTP ${response.status}`);
    }
    if (!response.body) throw new Error('This browser cannot read a streaming response.');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { frames, remainder } = parseFrames(buffer);
        buffer = remainder;
        for (const frame of frames) {
          if (frame.name === 'accepted') onAccepted?.(frame.payload);
          else if (frame.name === 'done') onDone?.(frame.payload);
          else onEvent?.(frame.payload);
        }
      }
    } finally {
      reader.cancel().catch(() => {});
    }
  },
};

export default crewPipelineCheckApi;
